import React, { useEffect, useState } from 'react';
import { ADMIN } from '@/lib/adminApi';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Eye, Mail, Phone, Reply } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = ['new', 'replied', 'closed'];
const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  replied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function AdminContacts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await ADMIN.contacts({ status: statusFilter || undefined, q: q || undefined });
      setRows(r.contacts || []);
    } catch { toast.error('Failed to load messages'); } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [statusFilter]);

  const onStatus = async (msg, status) => {
    try { await ADMIN.updateContactStatus(msg.id, status); toast.success('Status updated'); reload(); }
    catch { toast.error('Update failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-contacts-view">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Contact Messages</h1>
          <p className="text-sm text-slate-500 mt-1">General contact form submissions.</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="contacts-search" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && reload()} className="pl-9 w-full sm:w-64" />
          </div>
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="contacts-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}</SelectContent>
          </Select>
        </div>
      </header>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        {loading ? <Skeleton className="h-64" /> : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Company</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Contact</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No messages yet.</td></tr>}
                {rows.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50" data-testid={`contact-row-${m.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{m.full_name}</td>
                    <td className="px-4 py-3 text-slate-700">{m.company || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{m.inquiry_type}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="flex flex-col gap-0.5">
                        {m.phone && <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1 hover:text-[#0A2540]"><Phone className="h-3 w-3" />{m.phone}</a>}
                        {m.email && <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1 hover:text-[#0A2540] truncate max-w-[200px]"><Mail className="h-3 w-3" />{m.email}</a>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={m.status || 'new'} onValueChange={(v) => onStatus(m, v)}>
                        <SelectTrigger className={`h-8 w-28 capitalize text-xs font-semibold border ${STATUS_COLORS[m.status] || ''}`} data-testid={`contact-status-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3"><Button size="sm" variant="ghost" onClick={() => setSelected(m)} data-testid={`contact-view-${m.id}`}><Eye className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selected?.full_name} — <span className="font-normal text-slate-500">{selected?.inquiry_type}</span></DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Company" value={selected.company || '—'} />
                <Field label="Phone" value={selected.phone} link={`tel:${selected.phone}`} />
                <Field label="Email" value={selected.email} link={`mailto:${selected.email}`} />
                <Field label="Property Address" value={selected.property_address || '—'} />
              </div>
              <Field label="Message" value={selected.message} block />
              <div className="flex gap-2">
                <a href={`mailto:${selected.email}?subject=Re: Your inquiry to Elite Roofing`} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#0A2540] text-white text-sm font-semibold hover:bg-[#13294a]" data-testid="contact-reply"><Reply className="h-4 w-4" /> Reply via email</a>
                <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-100 text-slate-800 text-sm font-semibold hover:bg-slate-200"><Phone className="h-4 w-4" /> Call</a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, link, block }) {
  return (
    <div className={`rounded-lg bg-slate-50 p-3 ${block ? 'sm:col-span-2' : ''}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap break-words">
        {link ? <a href={link} className="text-[#0A2540] hover:underline">{value || '—'}</a> : (value || '—')}
      </div>
    </div>
  );
}
